/**
 * VMAP / break-plan normalizer — data/config driven schedules (not hard-coded UI offsets).
 * Accepts either a platform breaks[] payload or a minimal VMAP-like object.
 */

import { AdBreakKind } from "../Core/EntitlementModel";

/**
 * @param {string|null|undefined} kind
 * @returns {string}
 */
function mapKind(kind) {
  var k = String(kind || "").toLowerCase();
  if (k === "pre" || k === "pre_roll" || k === "preroll" || k === "start") {
    return AdBreakKind.PRE_ROLL;
  }
  if (k === "post" || k === "post_roll" || k === "postroll" || k === "end") {
    return AdBreakKind.POST_ROLL;
  }
  return AdBreakKind.MID_ROLL;
}

/**
 * Parse VMAP-style timeOffset ("start" | "end" | "HH:MM:SS[.mmm]" | seconds number).
 * @param {string|number|null} offset
 * @returns {{ kindHint: string|null, offsetSeconds: number|null }}
 */
export function parseTimeOffset(offset) {
  if (offset == null || offset === "") {
    return { kindHint: null, offsetSeconds: null };
  }
  if (typeof offset === "number" && isFinite(offset)) {
    return { kindHint: "mid_roll", offsetSeconds: offset };
  }
  var s = String(offset).trim().toLowerCase();
  if (s === "start" || s === "0" || s === "00:00:00" || s === "00:00:00.000") {
    return { kindHint: "pre_roll", offsetSeconds: null };
  }
  if (s === "end") {
    return { kindHint: "post_roll", offsetSeconds: null };
  }
  var parts = s.split(":");
  if (parts.length === 3) {
    var h = parseFloat(parts[0]);
    var m = parseFloat(parts[1]);
    var sec = parseFloat(parts[2]);
    if (isFinite(h) && isFinite(m) && isFinite(sec)) {
      return { kindHint: "mid_roll", offsetSeconds: h * 3600 + m * 60 + sec };
    }
  }
  var asNum = parseFloat(s);
  if (isFinite(asNum)) {
    return { kindHint: "mid_roll", offsetSeconds: asNum };
  }
  return { kindHint: null, offsetSeconds: null };
}

/**
 * Normalize BE / VMAP-like schedule into ordered breaks.
 *
 * @param {object} [input]
 * @param {object[]} [input.breaks]
 * @param {object[]} [input.adBreaks]
 * @param {string} [input.vmapTagUrl] opaque tag — returned as schedule.vmapTagUrl
 * @param {string} [input.vastTagUrl] single-tag fallback (pre-roll)
 * @returns {{ breaks: object[], vmapTagUrl: string|null, vastTagUrl: string|null }}
 */
export function normalizeVmapSchedule(input) {
  input = input || {};
  var vmapTagUrl = input.vmapTagUrl || input.vmap_tag_url || null;
  var vastTagUrl = input.vastTagUrl || input.vast_tag_url || null;
  var raw = input.breaks || input.adBreaks || input.ad_breaks || [];
  var breaks = [];

  if (Array.isArray(raw)) {
    for (var i = 0; i < raw.length; i++) {
      var row = raw[i] || {};
      var parsed = parseTimeOffset(
        row.offset_seconds != null
          ? row.offset_seconds
          : row.offsetSeconds != null
            ? row.offsetSeconds
            : row.timeOffset != null
              ? row.timeOffset
              : row.time_offset
      );
      var kind = mapKind(row.kind || row.breakType || parsed.kindHint);
      var tag =
        row.vast_tag_url ||
        row.vastTagUrl ||
        row.ad_url ||
        row.adTagUrl ||
        null;
      breaks.push({
        id: row.id != null ? String(row.id) : "break-" + i,
        kind: kind,
        offsetSeconds:
          kind === AdBreakKind.MID_ROLL
            ? parsed.offsetSeconds != null
              ? parsed.offsetSeconds
              : row.offset_seconds != null
                ? Number(row.offset_seconds)
                : null
            : null,
        vastTagUrl: tag,
        companions: Array.isArray(row.companions) ? row.companions.slice() : [],
      });
    }
  }

  if (!breaks.length && vastTagUrl) {
    breaks.push({
      id: "pre-default",
      kind: AdBreakKind.PRE_ROLL,
      offsetSeconds: null,
      vastTagUrl: vastTagUrl,
      companions: [],
    });
  }

  return {
    breaks: breaks,
    vmapTagUrl: vmapTagUrl ? String(vmapTagUrl) : null,
    vastTagUrl: vastTagUrl ? String(vastTagUrl) : null,
  };
}

export default {
  parseTimeOffset: parseTimeOffset,
  normalizeVmapSchedule: normalizeVmapSchedule,
};
